// ═══════════════════════════════════════════════════════════════════════════
// clasificarRentabilidadInferida — clasificador puro del dashboard VIOLETA
// "Rentabilidad inferida" (ml-rentabilidad-inferida, 15-jul-2026).
// ───────────────────────────────────────────────────────────────────────────
// Universo DISJUNTO de Contribución Neta: publicaciones ACTIVAS SIN venta en
// la ventana (CN trabaja las que SÍ vendieron, con datos reales). El margen
// se INFIERE con la fórmula completa de las órdenes reales (rentabilidadEstimada):
// comisión/envío por cascada cuenta → ecosistema + impuestos de la config
// fiscal. Fuente declarada SIEMPRE; sin costo cargado NO se inventa.
//
// Secciones (decisión Mariano 15-jul: MISMA acción — subir_precio al precio
// sugerido — en dos grupos para entender la urgencia):
//   perdida_inferida     → margen inferido < 0                 (prioridad ALTA)
//   bajo_minima_inferida → 0 ≤ margen < corte − histéresis     (prioridad MEDIA)
//   con_espacio          → informativa (el problema es tráfico, no precio)
//   sin_costo            → declarada (sembrar producto + cargar costo)
//
// GUARDAS DE EMISIÓN (dictamen arquitecto):
//   - Solo emite con COSTO PROPIO del producto vinculado (costoPromedio por
//     cascada de construirInfoRentabilidadPorItem). Comisión/envío pueden ser
//     inferidos (cuenta/eco) — van declarados en las fuentes, no frenan.
//     ⚠ Frescura del costo (<60-90d): pendiente — productos no tiene campo de
//     fecha de actualización del costo (deuda declarada en el doc).
//   - Histéresis (default 3 pts) SOLO en bajo_minima: una pub que oscila
//     alrededor del corte no entra y sale de la sección en cada corrida.
//     A pérdida NO tiene histéresis: pérdida es pérdida.
//   - Ads gastando sin vender: la pub NO proyecta ACOS esperado (la señal es
//     pausar el ad — lo trabaja Publicidad, capa 1 del flywheel) y queda
//     declarada por ítem (_adsGastanSinVender).
//
// PRECIO SUGERIDO (decisión Mariano): bisección sobre proyectarRentaFinalML —
// el precio mínimo con el que la renta FINAL (aplicando el descuento y ACOS
// ESPERADOS, las palancas que seguramente se prendan para que venda) llega a
// la rentabilidad mínima (o al equilibrio si no hay mínima). Un precio "justo
// en la mínima" de HOY volvía a quedar abajo del corte al prender palancas.
//
// PARIDAD CON LA VISTA: RentabilidadInferidaDashboard llama esta MISMA función
// (pubs canónicas → legacy con pubCanonicaALegacy, mismo input que el loader).
// ═══════════════════════════════════════════════════════════════════════════

import { construirInfoRentabilidadPorItem, metricasAdsPorItem } from './gatePromoRentabilidad'
import { estructuraCostosML, inferirRentabilidadML, proyectarRentaFinalML, resolverBenchmarksML } from '../rentabilidadEstimada'

const HISTERESIS_DEFAULT = 0.03   // 3 pts — dictamen: 3-5

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0 }

/**
 * Precio mínimo para que la renta FINAL (con palancas esperadas) alcance el
 * objetivo. Bisección con la MISMA proyección de la ficha/vista — comisión
 * proporcional al precio nuevo, envío por banda, impuestos config fiscal.
 * @returns {number|null} — null si ni cuadruplicando el precio se alcanza
 */
export function precioSugeridoInferido({ precio, costoUnit, categoryId = null, estructuraCuenta = null, ecosistema = null, impuestosEmpresa = null, acosSim = 0, descuentoSim = 0, objetivo = 0 } = {}) {
  const margenFinalA = (p) => proyectarRentaFinalML({
    precio: p, costoUnit, categoryId, estructuraCuenta, ecosistema, impuestosEmpresa,
    acosPct: acosSim, descuentoPct: descuentoSim,
  })?.margenFinalPct
  const obj = Number.isFinite(objetivo) ? objetivo : 0
  let lo = precio
  let hi = precio * 4
  const mHi = margenFinalA(hi)
  if (mHi == null || mHi < obj) return null
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2
    const m = margenFinalA(mid)
    if (m != null && m >= obj) hi = mid
    else lo = mid
  }
  return hi
}

/**
 * @param {object} input — mismo shape que arma el loader del agente
 * @param {Array}  input.publicaciones     — pubs legacy (pubCanonicaALegacy)
 * @param {Array}  input.ventas30d         — órdenes de la ventana (shape cascada)
 * @param {Array}  input.adsItems          — ads legacy {item_id, status, metrics{cost,…}} (mlAds)
 * @param {Array}  input.productos|catalogoLinks|categorias|marcas|proveedores|productoSourcing — cascada ADR 032
 * @param {Object} input.rentabilidadEmpresa — empresas/{id}/config/rentabilidad
 * @param {Object} input.config            — doc empresa (leemos _benchmarksML)
 * @param {Object} input.ecosistema        — conocimientoEcosistema/costos-ml (loader)
 * @param {Object} input.impuestosEmpresa  — config fiscal mergeada (loader _impuestos)
 * @param {Object} input.umbrales          — { histeresisPts }
 */
/**
 * PURA. De dónde salió el costo de ML en cada publicación inferida, contado.
 *
 * Se cuenta POR DATO (comisión y envío por separado) y no por publicación, y no
 * es un detalle: la cascada resuelve cada uno por su lado —el escalón "banda de
 * precio" existe **sólo para envío**, porque el umbral de envío gratis lo
 * mueve— así que una publicación puede tener la comisión de tu cuenta y el
 * envío de la mediana de su categoría. Un contador único por publicación tendría
 * que elegir uno de los dos y mentiría sobre el otro.
 */
export function repartoDeFuentes(secciones) {
  const vacio = () => ({ cuenta: 0, 'eco-categoria': 0, 'eco-banda': 0, 'eco-global': 0 })
  const out = { comision: vacio(), envio: vacio(), total: 0 }
  for (const sec of ['perdida_inferida', 'bajo_minima_inferida', 'con_espacio']) {
    for (const r of secciones[sec] || []) {
      const inf = r._inferida
      if (!inf) continue
      out.total++
      const c = inf.fuenteComision?.tipo
      const e = inf.fuenteEnvio?.tipo
      if (c && c in out.comision) out.comision[c]++
      if (e && e in out.envio) out.envio[e]++
    }
  }
  return out
}

export function clasificarRentabilidadInferida({
  publicaciones = [],
  ventas30d = [],
  adsItems = [],
  productos = [],
  catalogoLinks = [],
  categorias = [],
  marcas = [],
  proveedores = [],
  productoSourcing = [],
  rentabilidadEmpresa = null,
  config = {},
  ecosistema = null,
  impuestosEmpresa = null,
  umbrales = {},
} = {}) {
  const HISTERESIS = Number.isFinite(Number(umbrales.histeresisPts)) ? Number(umbrales.histeresisPts) : HISTERESIS_DEFAULT

  const infoRent = construirInfoRentabilidadPorItem({
    productos, catalogoLinks, categorias, marcas, proveedores, productoSourcing,
    empresaMin: rentabilidadEmpresa?.rentabilidadMinima ?? null,
  })
  const estructuraCuenta = estructuraCostosML(ventas30d)
  const metricsAdsMap = metricasAdsPorItem(adsItems)
  // Método de match del vínculo (auditoría del seed) — si el loader lo trae.
  const matchPorItem = new Map()
  for (const l of catalogoLinks) {
    if (l?.externalItemId && l?.metodoMatch) matchPorItem.set(String(l.externalItemId), l.metodoMatch)
  }

  // Ítems CON venta en la ventana → universo de Contribución Neta, afuera.
  const conVenta = new Set()
  for (const v of ventas30d) {
    for (const oi of (v.order_items || [])) {
      const id = oi?.item?.id
      if (id && num(oi.quantity) > 0) conVenta.add(String(id))
    }
  }

  const secciones = { perdida_inferida: [], bajo_minima_inferida: [], con_espacio: [], sin_costo: [] }
  let activasTotal = 0
  let noActivas = 0
  let conVentaCount = 0
  let adsGastanSinVender = 0
  let zonaHisteresis = 0

  for (const pub of publicaciones) {
    const itemId = String(pub.itemId ?? pub.id)
    const activa = pub.status === 'active' || pub.status === 'activa'
    if (!activa) { noActivas++; continue }
    activasTotal++
    if (conVenta.has(itemId)) { conVentaCount++; continue }

    const precio = num(pub.price)
    const stockRaw = pub.availableQuantity ?? pub.available_quantity
    const stock = stockRaw == null ? null : num(stockRaw)
    const categoryId = pub.datosCanal?.categoryId ?? null
    const rent = infoRent.get(itemId) || { costoUnit: null, corte: null, productoId: null }
    const gastoAds = num(metricsAdsMap.get(itemId)?.cost)
    if (gastoAds > 0) adsGastanSinVender++

    const base = {
      _itemId: itemId,
      id: itemId,
      itemId,
      _productoId: rent.productoId || null,
      _metodoMatch: matchPorItem.get(itemId) || null,
      _sku: pub.sellerSku ?? pub.sku ?? null,
      _ean: pub.ean ?? null,
      title: pub.title || itemId,
      permalink: pub.permalink || null,
      price: precio,
      _precio: precio,
      _stock: stock,
      _visitas30d: num(pub._visitas30d),
      _costoUnit: rent.costoUnit,
      _corte: rent.corte != null && rent.corte !== '' ? Number(rent.corte) : null,
      _capital: rent.costoUnit != null && stock != null ? stock * rent.costoUnit : 0,
      _adsGastanSinVender: gastoAds > 0,
      _gastoAds: gastoAds,
    }

    // GUARDA: sin costo propio del producto NO se infiere nada (no inventamos).
    // ⚠️ `_motivoSinInferir` distingue los DOS motivos que caen en la misma
    // sección, y no es cosmético: al usuario se le dice qué hacer, y "cargá el
    // costo" es un consejo FALSO para el segundo. Antes eran indistinguibles y
    // la descripción de la sección le hablaba a todos como si fuera el primero.
    if (!rent.costoUnit || precio <= 0) {
      secciones.sin_costo.push({ ...base, _motivoSinInferir: 'sin_costo' })
      continue
    }

    // ── HOY: fórmula completa. Sin venta no hay gasto de ads repartible por
    // unidad; si gastó, es la alerta roja (pausar_ad — no entra a la fórmula).
    const inferida = inferirRentabilidadML({
      precio, costoUnit: rent.costoUnit, categoryId,
      estructuraCuenta, ecosistema, impuestosEmpresa,
      corte: base._corte,
    })
    const margen = inferida?.margenPct ?? null
    // Tiene costo y precio, pero no hay de dónde sacar comisión ni envío: ni la
    // historia de la cuenta ni el ecosistema. NO se infiere (antes se fabricaba
    // un 0 % y el margen salía inflado). El costo ya está cargado, así que
    // pedirle que lo cargue sería mandarlo a hacer lo que ya hizo.
    if (margen == null) { secciones.sin_costo.push({ ...base, _motivoSinInferir: 'sin_referencia' }); continue }

    // ── SIMULACIÓN: palancas esperadas (solo las NO vivas — sin doble cobro).
    const bm = resolverBenchmarksML({ benchmarksEmpresa: config?._benchmarksML || null, ecosistema, categoryId })
    const precioOrig = Number(pub.originalPrice)
    let descPct = null
    let srcDesc = null
    if (Number.isFinite(precioOrig) && precioOrig > precio && precio > 0) {
      descPct = 1 - precio / precioOrig   // promo VIVA: ya está en el precio
      srcDesc = 'pub'
    } else if (bm?.descuentoPct != null) {
      descPct = bm.descuentoPct
      srcDesc = bm.fuenteDescuento?.tipo === 'cuenta' ? 'cuenta' : 'plataforma'
    }
    // ACOS esperado: sin ventas no hay ACOS propio; con ads gastando sin
    // vender NO se proyecta benchmark (la señal es pausar, no promediar).
    const acosPct = base._adsGastanSinVender ? null : (bm?.acosPct ?? null)
    const srcAcos = acosPct != null ? (bm?.fuenteAcos?.tipo === 'cuenta' ? 'cuenta' : 'plataforma') : null
    const dSim = srcDesc === 'pub' ? 0 : (descPct ?? 0)
    const acosSim = acosPct ?? 0
    const proy = proyectarRentaFinalML({
      precio, costoUnit: rent.costoUnit, categoryId,
      estructuraCuenta, ecosistema, impuestosEmpresa,
      acosPct: acosSim, descuentoPct: dSim,
    })
    const deltaFinal = (proy?.margenFinalPct != null && base._corte != null) ? proy.margenFinalPct - base._corte : null

    // Impuestos totales por unidad (columna − Imp. de la vista)
    const bd = inferida.breakdown || null
    const ganEst = (bd && bd.margenResidual != null && inferida.rentaNeta != null) ? (bd.margenResidual - inferida.rentaNeta) : 0
    const impTot = bd ? (num(bd.ivaNeto) + num(bd.iibb) + num(bd.impCredDebito) + num(ganEst)) : null

    const row = {
      ...base,
      _inferida: inferida,
      _margenPct: margen,
      _rentaNeta: inferida.rentaNeta,
      _impTot: impTot,
      _descPct: descPct,
      _srcDesc: srcDesc,
      _precioOriginal: Number.isFinite(precioOrig) ? precioOrig : null,
      _acosPct: acosPct,
      _srcAcos: srcAcos,
      _dSim: dSim,
      _proy: proy,
      _deltaFinal: deltaFinal,
    }

    const argsSugerido = { precio, costoUnit: rent.costoUnit, categoryId, estructuraCuenta, ecosistema, impuestosEmpresa, acosSim, descuentoSim: dSim, objetivo: base._corte ?? 0 }
    const conMetricas = (r) => ({
      ...r,
      // Payload para el recurso de la tarea (mismo shape que la vista 🎯).
      // `precioALograr`/`corte` siguen la convención que las vistas de tareas
      // YA renderizan (🎯 precio a lograr — misma línea que catálogo/buy-box).
      metricas: {
        precioActual: r.price ?? null,
        precioSugerido: r._precioSugerido ?? null,
        precioALograr: r._precioSugerido ?? null,
        corte: r._corte ?? null,
        margenInferidoPct: r._margenPct ?? null,
        rentaNetaUnidad: r._rentaNeta ?? null,
        rentaFinalPct: r._proy?.margenFinalPct ?? null,
        minimaPct: r._corte ?? null,
        costoUnit: r._costoUnit ?? null,
        fuente: 'inferida',
      },
    })

    if (margen < 0) {
      row._precioSugerido = precioSugeridoInferido(argsSugerido)
      row._motivo = `Margen inferido ${(margen * 100).toFixed(1)}% al precio actual (renta $${Math.round(inferida.rentaNeta ?? 0).toLocaleString('es-AR')}/u) — a PÉRDIDA sin haber vendido`
      secciones.perdida_inferida.push(conMetricas(row))
    } else if (base._corte != null && margen < base._corte - HISTERESIS) {
      row._precioSugerido = precioSugeridoInferido(argsSugerido)
      row._motivo = `Margen inferido ${(margen * 100).toFixed(1)}% por debajo de la mínima ${(base._corte * 100).toFixed(1)}%`
      secciones.bajo_minima_inferida.push(conMetricas(row))
    } else {
      // Zona de histéresis [corte−H, corte): declarada, no emite — evita que
      // una pub que oscila entre corridas entre y salga de la sección.
      if (base._corte != null && margen < base._corte) zonaHisteresis++
      if (deltaFinal != null && deltaFinal < 0) row._precioSugerido = precioSugeridoInferido(argsSugerido)
      secciones.con_espacio.push(row)
    }
  }

  const porCapital = (a, b) => (b._capital || 0) - (a._capital || 0) || (b.price || 0) - (a.price || 0)
  secciones.perdida_inferida.sort(porCapital)
  secciones.bajo_minima_inferida.sort(porCapital)
  secciones.con_espacio.sort((a, b) => (b._visitas30d || 0) - (a._visitas30d || 0))
  secciones.sin_costo.sort(porCapital)

  return {
    secciones,
    estructuraCuenta,
    analisis: {
      activasTotal,
      noActivas,
      conVentaCount,
      sinVenta: activasTotal - conVentaCount,
      adsGastanSinVender,
      zonaHisteresis,
      histeresisPts: HISTERESIS,
      perdida: secciones.perdida_inferida.length,
      bajoMinima: secciones.bajo_minima_inferida.length,
      conEspacio: secciones.con_espacio.length,
      sinCosto: secciones.sin_costo.length,
      // Los dos motivos, separados: "no cargaste el costo" (lo destraba el
      // usuario) vs "no hay referencia de comisión/envío" (lo destraba vender,
      // o que la plataforma junte muestra). Mezclarlos hace que la pantalla dé
      // un consejo falso a la mitad de las publicaciones.
      sinCostoCargado: secciones.sin_costo.filter(r => r._motivoSinInferir === 'sin_costo').length,
      sinReferencia: secciones.sin_costo.filter(r => r._motivoSinInferir === 'sin_referencia').length,
      // Reparto REAL de la cascada sobre lo que SÍ se infirió. Es lo que
      // alimenta el cartel: derivado del mismo cálculo que pinta la tabla, así
      // que no puede decir una cosa distinta de la que muestra.
      fuentes: repartoDeFuentes(secciones),
    },
    excluidos: {
      noActivas,
      conVentaCount,
      zonaHisteresis,
      adsGastanSinVender,
    },
  }
}
